/**
 * `/tasks/:id` — a task's dedicated single-pane tracker (Phase 1c).
 *
 * Reached by clicking a task on the `/tasks` board. Renders ONE read-only
 * snapshot of everything the PAs did for this task: a header (title / status /
 * @Task:<ref> / assignee / due / provenance) + a merged chronological TIMELINE
 * fusing the four task-keyed streams — work-log (pa_task_context), actions
 * (audit_log), approvals (pa_pending_actions), and messages (outbound_messages).
 * All from one aggregate fetch, `GET /api/tasks/:id`.
 *
 * The task's dragged-in `resources/` inputs and agent `deliverables/` outputs
 * live on the PA laptop; the runner syncs them into `pa_task_files` each tick
 * (migration 105), so this page shows them (with contents) in a Files section.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  api,
  ApiError,
  type TaskCharter,
  type TaskDetailResponse,
  type TaskFile,
  type TaskOnboardingQuestion,
  type TaskTimelineEntry,
} from "../lib/api";
import { studioFormat } from "../lib/studio-tz";
import { fmtBytes } from "../lib/format-bytes";

const DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

function fmtDateTime(iso: string | null): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : studioFormat(DATETIME_OPTS).format(d);
}

/** Map a status-ish string to a tag tone (green = good, red = bad). */
function statusTone(status: string): string {
  const s = status.toLowerCase();
  if (["active", "done", "completed", "approved", "sent", "ok"].includes(s)) {
    return "tag-success";
  }
  if (
    [
      "cancelled",
      "canceled",
      "failed",
      "rejected",
      "expired",
      "error",
    ].includes(s)
  ) {
    return "tag-error";
  }
  return "";
}

function Tag({ value }: { readonly value: string }): JSX.Element {
  return <span className={`tag ${statusTone(value)}`}>{value}</span>;
}

/**
 * The timeline is filtered by CATEGORY. Every entry maps to exactly one:
 * message / work / action (state mutations) / approval / read (list_·get_·sync
 * audit rows). `read` is hidden by default — it's the high-volume, no-state-change
 * noise — but the filter bar lets the operator switch any category on or off, so
 * "show me literally everything" is one tap away.
 */
type TimelineCategory = "message" | "work" | "action" | "approval" | "read";

const CATEGORY_ORDER: readonly TimelineCategory[] = [
  "message",
  "work",
  "action",
  "approval",
  "read",
];

const CATEGORY_META: Record<
  TimelineCategory,
  { readonly icon: string; readonly plural: string; readonly singular: string }
> = {
  message: { icon: "💬", plural: "Messages", singular: "Message" },
  work: { icon: "📝", plural: "Work notes", singular: "Work" },
  action: { icon: "🛠️", plural: "Actions", singular: "Action" },
  approval: { icon: "🔔", plural: "Approvals", singular: "Approval" },
  read: { icon: "👁", plural: "Reads & sync", singular: "Read" },
};

/** Reads/plumbing start hidden — they're the noise the filter exists to tame. */
const DEFAULT_HIDDEN: readonly TimelineCategory[] = ["read"];

/** Stable empty reference so the memo deps don't churn before data loads. */
const EMPTY_TIMELINE: ReadonlyArray<TaskTimelineEntry> = [];

function entryCategory(entry: TaskTimelineEntry): TimelineCategory {
  if (entry.type === "action") {
    return entry.action_kind === "read" ? "read" : "action";
  }
  return entry.type; // message | work | approval
}

function TimelineRow({
  entry,
}: {
  readonly entry: TaskTimelineEntry;
}): JSX.Element {
  const category = entryCategory(entry);
  const meta = CATEGORY_META[category];
  return (
    <li
      className="task-timeline-row"
      style={category === "read" ? rowStyleMuted : rowStyle}
    >
      <div style={gutterStyle} aria-hidden="true">
        {meta.icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={headStyle}>
          <span style={{ fontWeight: 600 }}>{meta.singular}</span>
          {entry.type === "work" && (
            <span className="tag">{entry.author}</span>
          )}
          {entry.type === "action" && (
            <>
              <code style={codeStyle}>{entry.action}</code>
              <Tag value={entry.outcome} />
            </>
          )}
          {entry.type === "approval" && (
            <>
              <code style={codeStyle}>{entry.action}</code>
              <Tag value={entry.status} />
            </>
          )}
          {entry.type === "message" && (
            <>
              <span className="tag">
                {entry.direction === "inbound" ? "you →" : "→ reply"}
              </span>
              <span className="tag">{entry.channel}</span>
              <Tag value={entry.status} />
            </>
          )}
          <span style={timeStyle}>{fmtDateTime(entry.at)}</span>
        </div>
        {entry.type === "work" && (
          <p style={bodyStyle}>{entry.content}</p>
        )}
        {entry.type === "action" && (
          <p style={metaLineStyle}>by {entry.actor}</p>
        )}
        {entry.type === "approval" && (
          <>
            <p style={bodyStyle}>{entry.summary}</p>
            <p style={metaLineStyle}>
              {entry.channel !== null ? `via ${entry.channel} · ` : ""}
              {entry.resolved_at !== null
                ? `resolved ${fmtDateTime(entry.resolved_at)}`
                : entry.expires_at !== null
                  ? `expires ${fmtDateTime(entry.expires_at)}`
                  : ""}
            </p>
          </>
        )}
        {entry.type === "message" && (
          <>
            <p style={bodyStyle}>{entry.body}</p>
            <p style={metaLineStyle}>
              {entry.direction === "inbound" ? "from " : "to "}
              {entry.recipient}
              {entry.source !== null ? ` · ${entry.source}` : ""}
            </p>
          </>
        )}
      </div>
    </li>
  );
}

/** Toggle chips — one per category that actually occurs — that let the operator
 *  choose what the timeline shows. A lit chip = shown; a dimmed chip = hidden. */
function TimelineFilterBar({
  counts,
  hidden,
  onToggle,
}: {
  readonly counts: Record<TimelineCategory, number>;
  readonly hidden: ReadonlySet<TimelineCategory>;
  readonly onToggle: (c: TimelineCategory) => void;
}): JSX.Element | null {
  const present = CATEGORY_ORDER.filter((c) => counts[c] > 0);
  if (present.length <= 1) return null; // nothing to choose between
  return (
    <div style={filterBarStyle}>
      {present.map((c) => {
        const on = !hidden.has(c);
        const meta = CATEGORY_META[c];
        return (
          <button
            key={c}
            type="button"
            onClick={() => onToggle(c)}
            aria-pressed={on}
            title={on ? `Hide ${meta.plural}` : `Show ${meta.plural}`}
            style={on ? filterChipOnStyle : filterChipOffStyle}
          >
            <span aria-hidden="true">{meta.icon}</span>
            <span>{meta.plural}</span>
            <span style={filterChipCountStyle}>{counts[c]}</span>
          </button>
        );
      })}
    </div>
  );
}

export function TaskDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<TaskDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [hidden, setHidden] = useState<Set<TimelineCategory>>(
    () => new Set<TimelineCategory>(DEFAULT_HIDDEN),
  );

  const timeline = data?.timeline ?? EMPTY_TIMELINE;
  // Per-category counts drive both the filter chips (which show a count and are
  // only rendered for categories that actually occur) and the "showing X of Y".
  const counts = useMemo<Record<TimelineCategory, number>>(() => {
    const c: Record<TimelineCategory, number> = {
      message: 0,
      work: 0,
      action: 0,
      approval: 0,
      read: 0,
    };
    for (const e of timeline) c[entryCategory(e)] += 1;
    return c;
  }, [timeline]);
  const visible = useMemo(
    () => timeline.filter((e) => !hidden.has(entryCategory(e))),
    [timeline, hidden],
  );

  const toggleCategory = (c: TimelineCategory): void => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  };

  useEffect(() => {
    if (id === undefined) {
      setError("missing_task_id");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .taskDetail(id)
      .then((r) => {
        if (cancelled) return;
        setData(r);
        setError(null);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(
          e instanceof ApiError ? (e.code ?? `http_${e.status}`) : e.message,
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return <p className="empty">Loading task…</p>;
  }
  if (error !== null) {
    const notFound = error === "task_not_found";
    return (
      <div>
        <p className="member-breadcrumb">
          <Link to="/tasks" className="data-member-link">
            ← Tasks
          </Link>
        </p>
        <p className="empty">
          {notFound
            ? "Task not found in this studio."
            : `Couldn't load this task (${error}).`}
        </p>
      </div>
    );
  }
  if (data === null) {
    return <p className="empty">Task not found.</p>;
  }

  const t = data.task;
  const assignee =
    t.assignee_kind === "unassigned"
      ? "Unassigned"
      : `${t.assignee_name ?? t.assignee_id ?? "?"} (${t.assignee_kind})`;

  return (
    <div>
      <p className="member-breadcrumb">
        <Link to="/tasks" className="data-member-link">
          ← Tasks
        </Link>
      </p>
      <h1 className="page-title member-title">
        {t.title} <Tag value={t.status} />
      </h1>
      <p className="member-breadcrumb" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {t.ref !== null && <code style={codeStyle}>@Task:{t.ref}</code>}
        <span>{assignee}</span>
        {t.category !== null && <span className="tag">{t.category}</span>}
        {t.due_at !== null && <span>due {fmtDateTime(t.due_at)}</span>}
      </p>
      {t.description !== null && t.description.length > 0 && (
        <p style={{ ...bodyStyle, marginBottom: 16 }}>{t.description}</p>
      )}
      <p style={metaLineStyle}>
        created {fmtDateTime(t.created_at)}
        {t.created_by !== null ? ` by ${t.created_by}` : ""} · updated{" "}
        {fmtDateTime(t.updated_at)}
      </p>

      <OnboardingBriefSection
        charter={data.charter}
        onboarding={data.onboarding}
        onboardingComplete={data.onboarding_complete}
      />

      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 10,
          flexWrap: "wrap",
          marginTop: 24,
        }}
      >
        <h2 className="section-title" style={{ margin: 0 }}>
          Timeline
        </h2>
        {data.timeline.length > 0 && (
          <span style={{ ...metaLineStyle, marginTop: 0 }}>
            showing {visible.length} of {data.timeline.length}
          </span>
        )}
      </div>

      {data.timeline.length > 0 && (
        <TimelineFilterBar
          counts={counts}
          hidden={hidden}
          onToggle={toggleCategory}
        />
      )}

      {data.timeline.length === 0 ? (
        <p className="empty">
          No work, actions, approvals, or messages recorded for this task yet.
        </p>
      ) : visible.length === 0 ? (
        <p className="empty">
          Everything is filtered out — switch a category back on above.
        </p>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {visible.map((entry, i) => (
            <TimelineRow key={`${entry.type}-${entry.at}-${i}`} entry={entry} />
          ))}
        </ul>
      )}

      <FilesSection files={data.files} />
    </div>
  );
}

type CharterTextKey =
  | "goal"
  | "success_criteria"
  | "tools_systems"
  | "data_needed"
  | "constraints"
  | "approver_stakeholders";

const CHARTER_FIELDS: ReadonlyArray<{
  key: CharterTextKey;
  label: string;
  /** The onboarding question's topic_key suffix (`onboard-<slug>-<marker>`). */
  marker: string;
}> = [
  { key: "goal", label: "Goal", marker: "goal" },
  { key: "success_criteria", label: "Success criteria", marker: "success" },
  { key: "tools_systems", label: "Tools / systems", marker: "tools" },
  { key: "data_needed", label: "Data needed", marker: "data" },
  { key: "constraints", label: "Constraints", marker: "constraints" },
  { key: "approver_stakeholders", label: "Approver / stakeholders", marker: "approver" },
];

/** Per-field answer state, merged from the charter value + its matching
 *  onboarding question (one source, no drift). */
type FieldState = "answered" | "open" | "skipped" | "auto";

const FIELD_STATE_BADGE: Record<FieldState, string> = {
  answered: "✓ Answered",
  open: "⚠ Awaiting your answer",
  skipped: "skipped",
  auto: "auto-filled",
};

/** Resolve a charter field's answer state from the onboarding question that
 *  fed it (matched by topic_key suffix). `auto` = the field was filled with no
 *  question asked; `open` = the operator still owes an answer (keeps the task
 *  red); a dismissed-but-answered question still counts as answered. */
function fieldState(
  marker: string,
  onboarding: ReadonlyArray<TaskOnboardingQuestion>,
): FieldState {
  const q = onboarding.find((x) => x.topic_key.endsWith(`-${marker}`));
  if (q === undefined) return "auto";
  if (q.status === "open") return "open";
  if (q.status === "answered") return "answered";
  const answered = q.answer_text !== null && q.answer_text.trim().length > 0;
  return answered ? "answered" : "skipped";
}

/** The onboarding section (migration 109) — ONE merged view: each of the six
 *  charter fields with its answer state (from the question that fed it), so the
 *  brief and the Q&A can never disagree. The worker may only start once every
 *  field is answered (no `open`) — an open field keeps the task red on the board.
 *  Hidden entirely when a task has neither a charter nor any onboarding questions. */
function OnboardingBriefSection({
  charter,
  onboarding,
  onboardingComplete,
}: {
  readonly charter: TaskCharter | null;
  readonly onboarding: ReadonlyArray<TaskOnboardingQuestion>;
  readonly onboardingComplete: boolean;
}): JSX.Element | null {
  if (charter === null && onboarding.length === 0) return null;
  return (
    <div style={{ marginTop: 24 }}>
      <h2 className="section-title">Onboarding</h2>
      {charter === null ? (
        <p className="empty" style={{ fontSize: "0.85em" }}>
          No charter recorded yet — onboarding is still in progress.
        </p>
      ) : (
        <>
          <p style={{ ...metaLineStyle, marginTop: 0 }}>
            {onboardingComplete
              ? "✅ Onboarding complete — the worker can start"
              : "🔴 Onboarding incomplete — an answer is still open, so the worker won't start"}
            {charter.created_by !== null ? ` · by ${charter.created_by}` : ""} ·
            updated {fmtDateTime(charter.updated_at)}
          </p>
          <dl className="charter-grid">
            {CHARTER_FIELDS.map((f) => {
              const text = charter[f.key].trim();
              const state = fieldState(f.marker, onboarding);
              return (
                <div key={f.key} className="charter-row">
                  <dt>
                    <span className="charter-field-label">{f.label}</span>
                    <span className={`onboarding-field-badge state-${state}`}>
                      {FIELD_STATE_BADGE[state]}
                    </span>
                  </dt>
                  <dd className={text.length === 0 ? "charter-empty" : ""}>
                    {text.length > 0 ? text : "—"}
                  </dd>
                </div>
              );
            })}
          </dl>
        </>
      )}
    </div>
  );
}

/** Inputs + outputs, synced from the PA laptop (migration 105). Two groups:
 *  agent OUTPUTS (deliverables/) and operator INPUTS (resources/); each file is
 *  click-to-expand its full contents. */
function FilesSection({
  files,
}: {
  readonly files: ReadonlyArray<TaskFile>;
}): JSX.Element {
  const outputs = files.filter((f) => f.kind === "deliverable");
  const inputs = files.filter((f) => f.kind === "resource");
  return (
    <div style={{ marginTop: 24 }}>
      <h2 className="section-title">Files</h2>
      <FileGroup
        title="Outputs — deliverables/"
        hint="what the PA produced for this task"
        files={outputs}
      />
      <FileGroup
        title="Inputs — resources/"
        hint="materials you dragged in for the PA to use"
        files={inputs}
      />
      {files.length === 0 && (
        <p className="empty" style={{ fontSize: "0.85em" }}>
          No files synced yet. The PA laptop syncs a task&apos;s{" "}
          <code style={codeStyle}>deliverables/</code> and{" "}
          <code style={codeStyle}>resources/</code> folders each tick.
        </p>
      )}
    </div>
  );
}

function FileGroup({
  title,
  hint,
  files,
}: {
  readonly title: string;
  readonly hint: string;
  readonly files: ReadonlyArray<TaskFile>;
}): JSX.Element | null {
  if (files.length === 0) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <p style={{ ...metaLineStyle, marginBottom: 4 }}>
        <strong>{title}</strong> · {hint}
      </p>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {files.map((f) => (
          <FileRow key={`${f.kind}-${f.rel_path}`} file={f} />
        ))}
      </ul>
    </div>
  );
}

function FileRow({ file }: { readonly file: TaskFile }): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  const hasContent = file.content !== null && file.content.length > 0;
  return (
    <li style={{ borderBottom: "1px solid var(--c-line-92-c)", padding: "8px 0" }}>
      <div style={headStyle}>
        <button
          type="button"
          onClick={() => hasContent && setOpen((v) => !v)}
          disabled={!hasContent}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            cursor: hasContent ? "pointer" : "default",
            font: "inherit",
            display: "flex",
            gap: 6,
            alignItems: "center",
          }}
          title={hasContent ? "Click to view contents" : "No inline preview"}
        >
          <span aria-hidden="true">{hasContent ? (open ? "▼" : "▶") : "•"}</span>
          <code style={codeStyle}>{file.rel_path}</code>
        </button>
        <span style={{ ...timeStyle, marginLeft: 8 }}>
          {fmtBytes(file.bytes)}
          {file.truncated ? " · too large to preview" : ""}
          {!hasContent && !file.truncated ? " · binary" : ""}
          {file.modified_at !== null ? ` · ${fmtDateTime(file.modified_at)}` : ""}
        </span>
      </div>
      {open && hasContent && (
        <pre
          style={{
            margin: "6px 0 0",
            padding: 10,
            background: "var(--c-tint-grey-12)",
            borderRadius: 6,
            overflowX: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontSize: "0.82em",
            maxHeight: 480,
            overflowY: "auto",
          }}
        >
          {file.content}
        </pre>
      )}
    </li>
  );
}

// --- inline layout styles (self-contained; the app is className-based but has
// no dedicated timeline class) ------------------------------------------------
const rowStyle: React.CSSProperties = {
  display: "flex",
  gap: 12,
  padding: "10px 0",
  borderBottom: "1px solid var(--c-line-92-c)",
};
// Reads/plumbing render dimmed so, even when switched on, they read as background.
const rowStyleMuted: React.CSSProperties = { ...rowStyle, opacity: 0.6 };
const filterBarStyle: React.CSSProperties = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  margin: "12px 0 8px",
};
const filterChipBase: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "4px 10px",
  borderRadius: 999,
  border: "1px solid var(--c-line-85)",
  font: "inherit",
  fontSize: "0.82em",
  cursor: "pointer",
  lineHeight: 1.4,
};
const filterChipOnStyle: React.CSSProperties = {
  ...filterChipBase,
  background: "var(--c-info-tint-a14)",
  borderColor: "var(--c-info-line-56)",
  opacity: 1,
};
const filterChipOffStyle: React.CSSProperties = {
  ...filterChipBase,
  background: "transparent",
  opacity: 0.5,
};
const filterChipCountStyle: React.CSSProperties = {
  fontVariantNumeric: "tabular-nums",
  opacity: 0.7,
  fontSize: "0.9em",
};
const gutterStyle: React.CSSProperties = {
  fontSize: "1.1em",
  width: 24,
  textAlign: "center",
  flexShrink: 0,
};
const headStyle: React.CSSProperties = {
  display: "flex",
  gap: 8,
  alignItems: "center",
  flexWrap: "wrap",
};
const timeStyle: React.CSSProperties = {
  marginLeft: "auto",
  fontSize: "0.8em",
  opacity: 0.7,
  whiteSpace: "nowrap",
};
const bodyStyle: React.CSSProperties = {
  margin: "4px 0 0",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};
const metaLineStyle: React.CSSProperties = {
  margin: "4px 0 0",
  fontSize: "0.85em",
  opacity: 0.7,
};
const codeStyle: React.CSSProperties = {
  fontFamily: "var(--mono, monospace)",
  fontSize: "0.85em",
  background: "var(--c-tint-grey-12)",
  padding: "1px 5px",
  borderRadius: 4,
};
